import { useState, useEffect } from "react";
import {
    useProject,
    useTask,
    useInspectionAttributes,
    useDocument,
    useLead,
    useMilestone,
} from "../../../../../hooks/query";
import { useAuth } from "../../../../../hooks/useAuth";
import { MILESTONE_KEY } from "../../../../../constants/milestone";

const standardTasks = [
    { name: "Initial Inspection", status: "In Progress", order: 1 },
    { name: "Estimate/Contract", status: "To Do", order: 2 },
    { name: "Contract Signing", status: "To Do", order: 3 },
    { name: "Building Permit", status: "To Do", order: 4 },
    { name: "Material Ordering", status: "To Do", order: 5 },
    { name: "Job Execution", status: "To Do", order: 6 },
    { name: "Final Settlement", status: "To Do", order: 7 },
];

export default function useCreateQuickProjectHandler() {
    const { useCreateQuickProject } = useProject();
    const { useCreate: useCreateTask } = useTask();
    const { useCreate: useCreateAttribute } = useInspectionAttributes();
    const { useInitialCreate: useCreateDocument } = useDocument();
    const { useCreateLead } = useLead();
    const { useUpsertMilestone } = useMilestone();

    const createQuickProjectSvc = useCreateQuickProject();
    const createTaskSvc = useCreateTask();
    const createAttributeSvc = useCreateAttribute();
    const createDocumentSvc = useCreateDocument();
    const createLeadSvc = useCreateLead();
    const upsertMilestoneSvc = useUpsertMilestone();
    const { user } = useAuth();

    const [progress, setProgress] = useState({ loading: false, message: "", error: null });

    const useInitializeForm = (setValue: any, getValues: any) => {
        useEffect(() => {
            const currentSalesman = getValues("salesmanName");
            const email = user?.Email || user?.email;
            if (!currentSalesman && email) {
                setValue(
                    "salesmanName",
                    {
                        value: email,
                        label: email,
                        ...user,
                    },
                    { shouldValidate: true, shouldDirty: true }
                );
            }
        }, [user, getValues, setValue]);
    };

    const createTasks = async (projectId: string) => {
        for (const task of standardTasks) {
            await createTaskSvc.mutateAsync({
                ProjectId: projectId,
                ParentTaskId: null,
                Name: task.name,
                Status: task.status,
                SortOrder: task.order,
            });
        }
    };

    const createMilestones = async (projectId: string) => {
        const milestones = [
            MILESTONE_KEY.INSPECTION,
            MILESTONE_KEY.ESTIMATE,
            MILESTONE_KEY.PERMIT_SUBMISSION,
            MILESTONE_KEY.MATERIAL_PURCHASE,
            MILESTONE_KEY.JOB_START,
            MILESTONE_KEY.ESTIMATED_COMPLETION,
        ];

        for (const key of milestones) {
            await upsertMilestoneSvc.mutateAsync({
                ProjectId: projectId,
                MilestoneKey: key,
                PlannedDate: null,
                ActualDate: null,
                Status: "PENDING",
            });
        }
    };

    const createMeasurements = async (projectId: string) => {
        const params = {
            ProjectId: projectId,
            Area: "0",
            Ridge: "0",
            Hip: "0",
            Valley: "0",
            Eave: "0",
            Rake: "0",
            "Step Flashing": "0",
            "Counter Flashing": "0",
            "Roof Vents": "0",
            "Box Vents": "0",
            "Turbine Vents": "0",
            "Plumbing Jacks": "0",
            "Furnace Vents": "0",
            "Bathroom Vents": "0",
            "Dryer Vents": "0",
            "Split Boots": "0",
            "Head Wall Metal": "0",
            "Roll Valley": "0",
            "Soffit Length": "0",
            Chimney: "0",
            Skylights: "0",
            "Open Patio/Soffits": "0",
            "Satellite Dish": "0",
            "Solar Panels": "0",
        };
        await createAttributeSvc.mutateAsync(params);
    };

    const createProjectEventHandler = async (data: any) => {
        try {
            setProgress({ loading: true, message: "Creating project...", error: null });

            const primarySalesman = data.salesmanName;

            const params = {
                Name: data.name,
                ProjectType: data.type,
                PrimarySalesmanId: primarySalesman?.Id || primarySalesman?.value || "",
                CustomerName: data.customerName,
                CustomerPhoneNumber: data.customerPhoneNumber,
                CustomerEmail: data.customerEmail || "",
                CustomerStreet: data.customerStreetAddress,
                CustomerCity: data.customerCity,
                CustomerState: data.customerState,
                CustomerZipCode: data.customerZipCode,
                CustomerCountry: data.customerCountry,
            };

            const projectDetails = await createQuickProjectSvc.mutateAsync(params);
            const projectId = projectDetails.Id as string;

            setProgress({ loading: true, message: "Setting up workflows & tasks...", error: null });

            // Create workflows
            await createMilestones(projectId);
            await createTasks(projectId);
            await createMeasurements(projectId);
            await createDocumentSvc.mutateAsync({ ProjectId: projectId });

            // Optional lead if passed
            const leadSource = data.leadSource;
            if (leadSource) {
                const leadName = data.secondaryLead || leadSource.Name || leadSource.label;
                await createLeadSvc.mutateAsync({
                    LeadSourcesId: leadSource.Id || leadSource.value,
                    Name: leadName,
                    ProjectId: projectId,
                });
            }

            setProgress({ loading: false, message: "Project created successfully!", error: null });
            return { projectId };
        } catch (e: any) {
            setProgress({ loading: false, message: "", error: e });
            console.error(e);
            return { projectId: null };
        }
    };

    return { createProjectEventHandler, progress, useInitializeForm };
}
